import { PostCard } from '@enuygun/frontend-components'

import { i18n, useTranslation } from 'next-i18next'
import Image from 'next/image'
import { useRef, useCallback, useEffect, type ReactNode } from 'react'

import Slider from 'components/Slider'
import SnapCarousel from 'components/SnapCarousel'
import { useAppState } from 'context/AppContext'
import { useIsMobile } from 'hooks/useIsMobile'

import { BlogPaths } from './data'
import {
  PopularTravelPostsContainer,
  PopularTravelPostsHeadContainer,
  PopularTravelPostsHeadWrapper,
  PopularTravelPostsHeading,
  SeeAllPopularTravelPosts,
} from './PopularTravelPosts.styled'
import { localeDateStringParser } from '../../utils/helper'
import { useIsClient } from 'hooks/useIsClient'
import { getAnalyticsRequestId } from 'utils/analyticsRequestId'
import sendTrackerLog from 'utils/tracker'

import { useDeviceType } from 'hooks/useDeviceType'

const SliderImage = ({ src, alt, height, width }) => {
  return <Image src={src} alt={alt} width={width} height={height} loading="lazy" />
}

/** Tracks clicks on the inner PostCard <a> without a JSX onClick on a non-interactive wrapper. */
const PostCardClickTracker = ({ onTrack, children, ...rest }: { onTrack: () => void; children: ReactNode; 'data-testid'?: string; style?: React.CSSProperties }) => {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const root = ref.current
    if (!root) return

    const handleClick = () => onTrack()
    root.addEventListener('click', handleClick)
    return () => root.removeEventListener('click', handleClick)
  }, [onTrack])

  return (
    <div ref={ref} {...rest}>
      {children}
    </div>
  )
}

type PostCardClickOptions = {
  slug: string
  title: string
  order: number
  authorName: string
  authorImageUrl: string
  postImageUrl: string
  articleDate: string
  updatedDate: string
}

const PopularTravelPosts = ({ travelArticles, isWingie = false, fullLocale = 'tr-tr' }) => {
  const isClient = useIsClient()
  const postCardData = travelArticles

  const isMobile = useIsMobile()
  const { isTablet } = useDeviceType()
  const { t } = useTranslation('common')

  const { host, market } = useAppState()
  const popularTravelPostsContainerRef = useRef<HTMLDivElement>(null)

  const getImage = (url, record: 'author' | 'post') => {
    if (isWingie) return url
    if (record === 'author') return `https://cdn2.enuygun.com/media/lib/175x175${url}`
    return `https://cdn2.enuygun.com/media/lib/352x153${url}`
  }

  const $data_test_id = isWingie ? 'wingie-homepage' : 'enuygun-homepage'

  const $blog_base = host + '/' + BlogPaths[isWingie ? 'wingie' : 'enuygun']

  const $see_all_button_link = $blog_base + (isWingie ? '/topic/travel-posts/' : '/konu/seyahat-yazilari/')

  const language = i18n?.language
  const isRTL = i18n?.dir(language?.includes('-') ? language.split('-')[1] : language) === 'rtl'

  // PopularTravelPosts card click tracking
  const handlePostCardClick = useCallback(
    ({ slug, title, order, authorName, authorImageUrl, postImageUrl, articleDate, updatedDate }: PostCardClickOptions) => {
      const environment = isMobile ? 'MOBILE_WEB' : 'DESKTOP_WEB'
      const requestId = getAnalyticsRequestId()

      sendTrackerLog({
        category: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        page: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
        type: 'click',
        message: 'popular_travel_posts_card_click',
        environment: environment,
        requestId,
        slug: slug,
        postTitle: title,
        cardOrder: order,
        authorName: authorName,
        authorImageUrl: authorImageUrl,
        postImageUrl: postImageUrl,
        articleDate: articleDate,
        updatedDate: updatedDate,
        isMobile: !!isMobile,
        market: market,
      })
    },
    [isMobile, market, isWingie]
  )

  // See all popular travel posts click tracking
  const handleSeeAllClick = useCallback(() => {
    const environment = isMobile ? 'MOBILE_WEB' : 'DESKTOP_WEB'
    const requestId = getAnalyticsRequestId()

    sendTrackerLog({
      category: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
      page: isWingie ? 'wingie-homepage' : 'enuygun-homepage',
      type: 'click',
      message: 'see_all_popular_travel_posts_click',
      environment: environment,
      requestId,
      linkUrl: $see_all_button_link,
      isMobile: !!isMobile,
      market: market,
    })
  }, [isMobile, market, isWingie, $see_all_button_link])

  const renderPostCards = () =>
    postCardData?.map((d, index) => {
      const authorName = `${d.author?.firstName || ''} ${d.author?.lastName || ''}`.trim()
      const authorImage = d.author?.authorInfo?.image || ''
      const postImage = d.image || ''
      const postTitle = d.title || ''
      const formattedAuthorImageUrl = getImage(authorImage, 'author')
      const formattedPostImageUrl = getImage(postImage, 'post')
      const formattedArticleDate = isClient ? localeDateStringParser(d.updated, fullLocale) : ''
      const updatedDate = d.updated || ''

      const trackPostCardClick = () =>
        handlePostCardClick({
          slug: d.slug || '',
          title: postTitle,
          order: index + 1,
          authorName,
          authorImageUrl: formattedAuthorImageUrl,
          postImageUrl: formattedPostImageUrl,
          articleDate: formattedArticleDate,
          updatedDate,
        })

      // PostCard renders an <a>; track via listener so the wrapper stays non-interactive (no role="button").
      return (
        <PostCardClickTracker key={d?.slug} style={{ background: '#fff' }} data-testid={`${$data_test_id}-${index}-post-card`} onTrack={trackPostCardClick}>
          <PostCard
            href={`${$blog_base}/${d.slug || ''}/`}
            slug={d.slug || ''}
            authorImage={<SliderImage width={40} height={40} src={formattedAuthorImageUrl} alt={authorName} data-testid={`${$data_test_id}-author-image-${index}`} />}
            image={<SliderImage width={352} height={153} src={formattedPostImageUrl} alt={postTitle} data-testid={`${$data_test_id}-post-image-${index}`} />}
            viewCount={d.postView?.count}
            title={postTitle}
            articeDate={formattedArticleDate}
            tags={d.topics || []}
            author={authorName}
            dataTestId={`${$data_test_id}-${index}-link`}
          />
        </PostCardClickTracker>
      )
    })

  return (
    postCardData && (
      <PopularTravelPostsContainer ref={popularTravelPostsContainerRef}>
        <PopularTravelPostsHeadContainer>
          <PopularTravelPostsHeading data-testid={`${$data_test_id}-heading`}>{t('popularTravelPosts.heading')}</PopularTravelPostsHeading>
          {!isMobile && (
            <SeeAllPopularTravelPosts data-testid={`${$data_test_id}-articleSeeAll`} href={$see_all_button_link} target="_blank" onClick={handleSeeAllClick}>
              {t('popularTravelPosts.seeAllButtonText')}
            </SeeAllPopularTravelPosts>
          )}
        </PopularTravelPostsHeadContainer>
        <PopularTravelPostsHeadWrapper isMobile={isMobile} isRTL={isRTL}>
          {isMobile ? (
            <SnapCarousel
              options={{
                perPage: isTablet ? 2.67 : 1.34,
                gap: 16,
                hasLastChildPadding: true,
              }}
              isRTL={isRTL}
              dataTestId={`${$data_test_id}-popular-travel-posts-carousel`}
              containerWidth={1120}
              isMobile={true}
            >
              {renderPostCards()}
            </SnapCarousel>
          ) : (
            <Slider isRTL={isRTL} options={{ fixedWidth: undefined, perPage: 3, padding: 16, gap: 32, arrows: false }} dataTestId={`${$data_test_id}-popular-travel-posts-desktop`}>
              {renderPostCards()}
            </Slider>
          )}
        </PopularTravelPostsHeadWrapper>
        {isMobile && (
          <SeeAllPopularTravelPosts data-testid={`${$data_test_id}-articleSeeAll-mobile`} href={$see_all_button_link} target="_blank" isTablet={isTablet} onClick={handleSeeAllClick}>
            {t('popularTravelPosts.seeAllButtonText')}
          </SeeAllPopularTravelPosts>
        )}
      </PopularTravelPostsContainer>
    )
  )
}

export default PopularTravelPosts
